
// -----------------------------------------------------------------------------
// src/features/transactions/transactionSlice.js
// -----------------------------------------------------------------------------
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchTransactionsThunk,
  updateTransactionThunk,
  fetchSingleTransactionThunk
} from './transactionThunks';

const initialState = {
  transactions: [],
  selectedTransaction: null,
  loading: false,
  singleLoading: false,
  error: null,
  next: null,
  count: 0,
  storeFilter: null,
};

const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    resetTransactions(state) {
      state.transactions = [];
      state.next = null;
      state.count = 0;
      state.error = null;
      state.storeFilter = null;
    },
    resetSelectedTransaction(state) {
      state.selectedTransaction = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchAll
      .addCase(fetchTransactionsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { results, next, count } = action.payload;
        const offset = action.meta.arg?.offset ?? 0;
        if (offset > 0) {
          // infinite‑scroll – append
          state.transactions.push(...results);
        } else {
          // fresh load – replace
          state.transactions = results;
        }
        state.next = next;
        state.count = count;
        // Use store_id if provided, fall back to storeId for backward compatibility
        state.storeFilter = action.meta.arg?.store_id || action.meta.arg?.storeId || null;
      })
      .addCase(fetchTransactionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchSingle
      .addCase(fetchSingleTransactionThunk.pending, (state) => {
        state.singleLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleTransactionThunk.fulfilled, (state, action) => {
        state.singleLoading = false;
        state.selectedTransaction = action.payload;
        
        // Update in the list if it exists there
        const idx = state.transactions.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) {
          state.transactions[idx] = action.payload;
        }
      })
      .addCase(fetchSingleTransactionThunk.rejected, (state, action) => {
        state.singleLoading = false;
        state.error = action.payload;
      })
      
      // update
      .addCase(updateTransactionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransactionThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        
        // Update in the main list if it exists there
        const idx = state.transactions.findIndex((t) => t.id === updated.id);
        if (idx !== -1) {
          state.transactions[idx] = updated;
        }
        
        // Update the selected transaction if it's the one that was updated
        if (state.selectedTransaction && state.selectedTransaction.id === updated.id) {
          state.selectedTransaction = updated;
        }
      })
      .addCase(updateTransactionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetTransactions, resetSelectedTransaction } = transactionSlice.actions;
export default transactionSlice.reducer;